import { __ } from '@wordpress/i18n';
import { setupLocationVisibility } from './cityFilter';
import { initOrgMembershipSection } from './membershipRepeater';
import { initOrgSolvencySection } from './solvencyRepeater';
import { initOrgProductSection } from './productRepeater';
import { initOrgSizeSection } from './sizeRepeater';
import { initLocationPicker } from './locationPicker';

const GROUPS = [
	{
		label: __( 'Basic information', 'anacrm' ),
		description: __(
			'Description, sector, field, work scope, keywords, legal form, establishment year.',
			'anacrm'
		),
		open: false,
		fields: [
			'_or_description',
			'_or_sector',
			'_or_field',
			'_or_work_scope',
			'_or_keyword',
			'_or_legal_form',
			'_or_year_establishment',
			'_or_person',
			'_or_handicaped',
			'_or_image',
		],
	},
	{
		label: __( 'Location', 'anacrm' ),
		description: __( 'Territory, city, address.', 'anacrm' ),
		fields: [
			'_or_location',
			'_or_territory',
			'_or_region',
			'_or_city',
			'_or_address',
			'_or_lat',
			'_or_lng',
		],
	},
	{
		label: __( 'Interests', 'anacrm' ),
		description: __( 'ESS, internships, voluntary work.', 'anacrm' ),
		fields: [ '_or_ute', '_or_internships', '_or_voluntary_work' ],
	},
	{
		label: __( 'Contact', 'anacrm' ),
		description: __(
			'Contact person, email, phone, website, Social networks.',
			'anacrm'
		),
		fields: [
			'_or_person_ref',
			'_or_space_ref',
			'_or_contact_mail',
			'_or_contact_phone',
			'_or_contact_website',
			'_or_contact_social',
		],
	},
	{
		label: __( 'Membership', 'anacrm' ),
		description: __( 'Networks.', 'anacrm' ),
		fields: [
			'_or_network',
			'_or_anagos_membership',
			'_or_anagos_year_membership',
		],
	},
	{
		label: __( 'Relations', 'anacrm' ),
		description: __(
			'Relations with organizations, spaces, projects and resources.',
			'anacrm'
		),
		fields: [ '_or_org', '_or_space', '_or_project', '_or_resource' ],
	},
	{
		label: __( 'Seats', 'anacrm' ),
		description: __( '', 'anacrm' ),
		fields: [ '_or_seat_2025', '_or_seat_2024', '_or_seat_2023' ],
	},
	{
		label: __( 'Audit', 'anacrm' ),
		description: __( '', 'anacrm' ),
		fields: [ '_or_audit', '_or_audit_file', '_or_social_market' ],
	},
	{
		label: __( 'Social market spending', 'anacrm' ),
		description: __( '', 'anacrm' ),
		fields: [
			'_or_audit_social_market_spending_2025',
			'_or_audit_social_market_spending_2024',
			'_or_audit_social_market_spending_2023',
		],
	},
];

function findFieldWrapper( form, name ) {
	// Pods adds class pods-form-ui-row-name-{name} to every field container.
	// Underscores in the field name are converted to dashes in the class,
	// so _or_sector → pods-form-ui-row-name--or-sector (double dash from leading _).
	const dashedName = name.replace( /_/g, '-' );
	return form.querySelector( `.pods-form-ui-row-name-${ dashedName }` );
}

// Shows/hides the ANAGOS membership fields depending on whether the org's
// selected networks (_or_network) include the "Red ANAGOS" term configured
// in the ANACRM_SETTINGS form_org_network_anagos option.
function setupAnagosMembershipVisibility( form, anagosTermId ) {
	const networkWrapper = findFieldWrapper( form, '_or_network' );
	const membershipWrapper = findFieldWrapper( form, '_or_anagos_membership' );
	const yearWrapper = findFieldWrapper( form, '_or_anagos_year_membership' );

	if (
		! networkWrapper ||
		! anagosTermId ||
		( ! membershipWrapper && ! yearWrapper )
	) {
		return;
	}

	function applyVisibility() {
		const selectedIds = [
			...networkWrapper.querySelectorAll(
				'input[type="hidden"][name^="pods_field__or_network"]'
			),
		].map( ( el ) => parseInt( el.value ) );

		const isAnagosNetwork = selectedIds.includes( anagosTermId );

		if ( membershipWrapper ) {
			membershipWrapper.style.display = isAnagosNetwork ? '' : 'none';
		}
		if ( yearWrapper ) {
			yearWrapper.style.display = isAnagosNetwork ? '' : 'none';
		}
	}

	applyVisibility();

	new MutationObserver( applyVisibility ).observe( networkWrapper, {
		childList: true,
		subtree: true,
	} );
}

// Public fields are the ones shown in the read-only org view
// (anacrm_org_claim_visible_fields() → window.anacrm.orgPublicFields);
// everything else is private. 'public' | 'mixed' | 'private'.
const PRIVACY_STYLES = {
	public: {
		details: 'border-green',
		summary: 'bg-green/30 hover:bg-green/60',
		body: 'bg-green/10',
	},
	mixed: {
		details: 'border-gray',
		summary: 'bg-gray/30 hover:bg-gray/60',
		body: 'bg-gray/10',
	},
	private: {
		details: 'border-yellow',
		summary: 'bg-yellow/30 hover:bg-yellow/60',
		body: 'bg-yellow/10',
	},
};

function getPrivacy( fieldNames ) {
	const publicFields = window.anacrm?.orgPublicFields ?? [];
	const total = fieldNames.length;
	const nPublic = fieldNames.filter( ( f ) =>
		publicFields.includes( f.replace( /^_or_/, '' ) )
	).length;
	if ( nPublic === total ) {
		return 'public';
	}
	return nPublic === 0 ? 'private' : 'mixed';
}

function privacyNote( privacy ) {
	if ( privacy === 'private' ) {
		return __( "This information won't be public", 'anacrm' );
	}
	if ( privacy === 'mixed' ) {
		return __( "Some of these fields won't be public", 'anacrm' );
	}
	return '';
}

// Builds a <details> box (header + body) coloured by its privacy level.
function createBox( { label, description, privacy, open = false } ) {
	const styles = PRIVACY_STYLES[ privacy ];

	const details = document.createElement( 'details' );
	if ( open ) {
		details.open = true;
	}
	details.className = `mb-4 border-2 ${ styles.details }`;

	const summary = document.createElement( 'summary' );
	summary.className = `px-4 py-3 cursor-pointer select-none marker:float-left ${ styles.summary }`;

	const note = privacyNote( privacy );
	if ( note ) {
		const noteEl = document.createElement( 'span' );
		noteEl.className = 'float-right text-xs font-semibold';
		noteEl.textContent = note;
		summary.appendChild( noteEl );
	}

	const title = document.createElement( 'h2' );
	title.className = 'text-base font-semibold inline-block';
	title.textContent = label;
	summary.appendChild( title );
	const desc = document.createElement( 'p' );
	desc.textContent = description;
	summary.appendChild( desc );
	details.appendChild( summary );

	const body = document.createElement( 'div' );
	body.className = `px-4 py-2 ${ styles.body }`;
	details.appendChild( body );

	return { details, body };
}

function markPrivateField( wrapper ) {
	wrapper.classList.add( 'border-l-4', 'border-yellow', 'pl-3', 'my-2' );
	const badge = document.createElement( 'span' );
	badge.className =
		'inline-block mb-1 px-2 text-xs font-semibold bg-yellow/30';
	badge.textContent = __( 'Private', 'anacrm' );
	wrapper.insertBefore( badge, wrapper.firstChild );
}

function buildAccordion( form ) {
	for ( const group of GROUPS ) {
		const fieldWrappers = group.fields
			.map( ( f ) => findFieldWrapper( form, f ) )
			.filter( Boolean );
		if ( ! fieldWrappers.length ) {
			continue;
		}

		const parent = fieldWrappers[ 0 ].parentElement;

		const privacy = getPrivacy( group.fields );
		const { details, body } = createBox( {
			label: group.label,
			description: group.description,
			privacy,
			open: group.open,
		} );

		parent.insertBefore( details, fieldWrappers[ 0 ] );
		const publicFields = window.anacrm?.orgPublicFields ?? [];
		fieldWrappers.forEach( ( w ) => {
			body.appendChild( w );
		} );
		if ( privacy === 'mixed' ) {
			group.fields.forEach( ( f ) => {
				const w = findFieldWrapper( form, f );
				if (
					w &&
					! publicFields.includes( f.replace( /^_or_/, '' ) )
				) {
					markPrivateField( w );
				}
			} );
		}
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	const wrapper = document.querySelector( '.anacrm-org-form-accordion' );
	if ( ! wrapper ) {
		return;
	}

	const successDiv = wrapper.querySelector( '.pods-form-front-success' );
	if ( successDiv ) {
		const btn = document.createElement( 'button' );
		btn.type = 'button';
		btn.className = 'px-3 py-2 text-2xl hover:text-yellow cursor-pointer';
		btn.innerHTML = '&times;';
		btn.addEventListener( 'click', () => successDiv.remove() );
		successDiv.appendChild( btn );
	}

	const form = wrapper.querySelector( 'form' );
	if ( ! form ) {
		return;
	}

	let done = false;

	function tryBuild() {
		if ( done ) {
			return;
		}
		if ( ! form.querySelector( '.pods-field__container' ) ) {
			return;
		}
		done = true;
		observer.disconnect();
		buildAccordion( form );
		setupLocationVisibility( form, {
			locationField: '_or_location',
			territoryField: '_or_territory',
			regionField: '_or_region',
			cityField: '_or_city',
			addressField: '_or_address',
		} );
		initLocationPicker( form, { prefix: '_or_' } );
		setupAnagosMembershipVisibility(
			form,
			parseInt( wrapper.dataset.networkAnagosTermId ?? '0' )
		);

		// Suggestions only cover the visible fields: no people, solvency, products or sizes.
		if ( wrapper.dataset.isSuggestion === '1' ) {
			return;
		}

		const orgId = wrapper.dataset.orgId ?? '';
		const isNew = wrapper.dataset.isEdit === '0';

		const submittableFields = form.querySelector(
			'.pods-submittable-fields'
		);
		const submitRow = submittableFields
			? submittableFields.querySelector( '.pods-submit' )
			: null;
		function insertBox( box ) {
			if ( submitRow ) {
				submittableFields.insertBefore( box, submitRow );
			} else if ( submittableFields ) {
				submittableFields.appendChild( box );
			} else {
				form.appendChild( box );
			}
		}

		// People and solvency are never shown publicly; products and sizes are.
		const people = createBox( {
			label: __(
				'Who are the people associated with the organization?',
				'anacrm'
			),
			description: '',
			privacy: 'private',
		} );
		insertBox( people.details );
		initOrgMembershipSection( people.body, orgId, isNew );

		const solvency = createBox( {
			label: __( 'Solvency for Public Procurement', 'anacrm' ),
			description: __( 'Financial and technical solvency', 'anacrm' ),
			privacy: 'private',
		} );
		insertBox( solvency.details );
		const orgFieldWrapper = findFieldWrapper( form, '_or_field' );
		initOrgSolvencySection( solvency.body, orgId, isNew, orgFieldWrapper );

		const products = createBox( {
			label: __( 'Products and services', 'anacrm' ),
			description: __(
				'Products and services offered by the organization',
				'anacrm'
			),
			privacy: 'public',
		} );
		insertBox( products.details );
		initOrgProductSection( products.body, orgId, isNew, orgFieldWrapper );

		const size = createBox( {
			label: __( 'Size', 'anacrm' ),
			description: __( 'Number of people, by year and type', 'anacrm' ),
			privacy: 'public',
		} );
		insertBox( size.details );
		initOrgSizeSection( size.body, orgId, isNew );
	}

	let debounceTimer = null;
	const observer = new MutationObserver( () => {
		clearTimeout( debounceTimer );
		debounceTimer = setTimeout( tryBuild, 300 );
	} );

	observer.observe( form, { childList: true, subtree: true } );

	tryBuild();
} );
